import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FeatureGate from '../components/FeatureGate';
import ContactManager from '../components/ContactManager';
import EmailOutreach from '../components/EmailOutreach';
import CommunicationHistory from '../components/CommunicationHistory';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import supabase from '../supabase/supabase';

const { FiUsers, FiMail, FiMessageSquare, FiLoader } = FiIcons;

function Communications() {
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const [activeTab, setActiveTab] = useState('contacts');
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    loadAllData();
  }, [user, getActiveUserId()]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const activeUserId = getActiveUserId();

      // Load Contacts
      const { data: contactsData, error: contactsError } = await supabase
        .from('contacts')
        .select('*')
        .eq('user_id', activeUserId)
        .order('last_name', { ascending: true });
      if (contactsError) throw contactsError;
      setContacts(contactsData || []);

      // Load Communication Logs
      const { data: logsData, error: logsError } = await supabase
        .from('communication_log')
        .select('*')
        .eq('user_id', activeUserId)
        .order('timestamp', { ascending: false });
      if (logsError) throw logsError;
      setLogs(logsData || []);

      // Load Saved Schools for linking
      const { data: savedData, error: savedError } = await supabase
        .from('saved_schools')
        .select('*')
        .eq('user_id', activeUserId);
      if (savedError) throw savedError;

      if (savedData && savedData.length > 0) {
        const featuredIds = savedData.filter(s => s.school_type === 'featured').map(s => s.school_id);
        const customIds = savedData.filter(s => s.school_type === 'custom').map(s => s.school_id);

        let allSchools = [];
        if (featuredIds.length > 0) {
          const { data: featured } = await supabase.from('featured_schools').select('id, name').in('id', featuredIds);
          if (featured) {
            allSchools = [...allSchools, ...featured.map(school => ({
              ...school,
              saved_id: savedData.find(s => s.school_type === 'featured' && s.school_id === school.id).id
            }))];
          }
        }

        if (customIds.length > 0) {
          const { data: custom } = await supabase.from('user_schools').select('id, name').in('id', customIds);
          if (custom) {
            allSchools = [...allSchools, ...custom.map(school => ({
              ...school,
              saved_id: savedData.find(s => s.school_type === 'custom' && s.school_id === school.id).id
            }))];
          }
        }
        setSchools(allSchools);
      } else {
        setSchools([]);
      }

    } catch (err) {
      console.error('Error loading communications data:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'contacts', label: 'Contact List', icon: FiUsers },
    { id: 'outreach', label: 'Email Outreach', icon: FiMail },
    { id: 'history', label: 'Activity Log', icon: FiMessageSquare }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SafeIcon icon={FiLoader} className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <FeatureGate feature="Outreach & CRM">
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-8">
            <h1 className="text-3xl font-space-grotesk font-bold text-secondary mb-2">
              Recruiting Communications
            </h1>
            <p className="text-gray-600">
              Manage your coach network, send targeted emails, and track your outreach history.
            </p>
          </div>

          <div className="border-b border-gray-200 mb-8">
            <nav className="flex space-x-6 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'contacts' && (
              <ContactManager 
                contacts={contacts} 
                schools={schools} 
                onContactsUpdated={loadAllData} 
              />
            )}
            
            {activeTab === 'outreach' && (
              <EmailOutreach 
                contacts={contacts} 
                schools={schools}
                onLogAdded={loadAllData}
              />
            )}

            {activeTab === 'history' && (
              <CommunicationHistory 
                logs={logs}
                contacts={contacts}
                onLogAdded={loadAllData}
              />
            )}
          </motion.div>

        </div>
      </div>
    </FeatureGate>
  );
}

export default Communications;